/**
 * The wizard's second step, Steer (docs/plans/lms-course-builder-plan.md 8.2
 * step 2, 6.2, 6.4, 6.10, 18.2 Q12; WP-B30).
 *
 * Mode new: the title (optional; the builder proposes one when blank), the
 * category (every category but SOPs), the kind (quiz or competency; a
 * scenario is a quiz), the validity in months (empty: never expires) and the
 * renewal lead days (empty: the network default), the audience line, the
 * target length (30, 45, 60 or 90 minutes, with the number of sections it
 * implies; 3 to 6 for a scenario), the pool size (20, 30 or 45 with the draw
 * at half; 3 to 6 drawn in full for a scenario) and an optional instruction.
 *
 * Mode extend: the course is fixed; the current version's sections are
 * listed with "Revise with the new material" (unticked), then "Revise the
 * closing section" (ticked) and the instruction. The profile hint names the
 * rules the builder is held to (the one the editor shows).
 */
import { useId } from 'react';
import { BUILDER_AUDIENCE_MAX, BUILDER_INSTRUCTION_MAX } from '../../../../api/builder';
import type { CourseCategory, CourseKind } from '../../../../content/types';
import { Checkbox, NumberInput, SelectField, TextArea, TextInput } from '../fields';
import { CATEGORY_LABELS, KIND_LABELS, profileHint } from '../model';
import { cardCls, hintCls, microLabelCls, subheadingCls } from '../styles';
import {
  BUILDER_CATEGORY_ORDER,
  BUILDER_KINDS,
  defaultSizes,
  drawOf,
  isScenarioCategory,
  minutesOptions,
  poolOptions,
  sectionsImplied,
} from './runModel';

/** The steering a new course takes (Appendix D targets, mode new). */
export interface SteerNew {
  title: string;
  category: CourseCategory;
  kind: CourseKind;
  validityMonths: number | null;
  renewalLeadDays: number | null;
  audience: string;
  targetMinutes: number;
  poolSize: number;
  instruction: string;
}

/** The steering an extend run takes (6.10). */
export interface SteerExtend {
  audience: string;
  replaceSectionIds: string[];
  reviseClosing: boolean;
  instruction: string;
}

/** One section of the current version, as the extend list shows it. */
export interface SectionChoice {
  id: string;
  title: string;
  /** The closing section "Putting it into practice at the site" is revised through its own box. */
  closing?: boolean;
}

export function blankSteerNew(): SteerNew {
  const sizes = defaultSizes('compliance');
  return {
    title: '',
    category: 'compliance',
    kind: 'quiz',
    validityMonths: 12,
    renewalLeadDays: null,
    audience: '',
    targetMinutes: sizes.minutes,
    poolSize: sizes.pool,
    instruction: '',
  };
}

export function blankSteerExtend(): SteerExtend {
  return { audience: '', replaceSectionIds: [], reviseClosing: true, instruction: '' };
}

/** A category change moves the sizes onto that category's lists (18.2 Q12) and a scenario onto quiz and no expiry. */
export function withCategory(steer: SteerNew, category: CourseCategory): SteerNew {
  const next: SteerNew = { ...steer, category };
  const minutes = minutesOptions(category);
  const pools = poolOptions(category);
  const sizes = defaultSizes(category);
  if (!minutes.includes(next.targetMinutes)) next.targetMinutes = sizes.minutes;
  if (!pools.includes(next.poolSize)) next.poolSize = sizes.pool;
  if (isScenarioCategory(category)) {
    next.kind = 'quiz';
    next.validityMonths = null;
    next.renewalLeadDays = null;
  }
  return next;
}

export type SteerStepProps =
  | {
      mode: 'new';
      value: SteerNew;
      onChange: (value: SteerNew) => void;
    }
  | {
      mode: 'extend';
      value: SteerExtend;
      onChange: (value: SteerExtend) => void;
      courseTitle: string;
      category: CourseCategory;
      sections: SectionChoice[];
    };

function InstructionField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <TextArea
      label={`Instruction for the builder (optional, ${BUILDER_INSTRUCTION_MAX} characters)`}
      value={value}
      onChange={(v) => onChange(v.slice(0, BUILDER_INSTRUCTION_MAX))}
      rows={3}
      testId="lms-builder-instruction"
      placeholder="For example: write for new CRCs; stress the reporting timeline"
      hint={`${value.length} of ${BUILDER_INSTRUCTION_MAX}`}
    />
  );
}

function AudienceField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <TextInput
      label="Who the course is for (optional)"
      value={value}
      onChange={(v) => onChange(v.slice(0, BUILDER_AUDIENCE_MAX))}
      placeholder="Coordinators and regulatory staff"
      testId="lms-builder-audience"
    />
  );
}

export function SteerStep(props: SteerStepProps) {
  const headingId = useId();

  if (props.mode === 'extend') {
    const { value, onChange, sections, courseTitle, category } = props;
    const teaching = sections.filter((s) => !s.closing);
    const toggle = (id: string, on: boolean) =>
      onChange({
        ...value,
        replaceSectionIds: on ? [...value.replaceSectionIds.filter((x) => x !== id), id] : value.replaceSectionIds.filter((x) => x !== id),
      });
    return (
      <div className="space-y-4" data-testid="lms-builder-steer">
        <section className={`${cardCls} space-y-3`} aria-labelledby={headingId}>
          <h3 id={headingId} className={subheadingCls}>
            {`Add the new material to ${courseTitle}`}
          </h3>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            The builder writes a new draft version. Sections you do not tick are kept exactly as they are; new material becomes new sections. Nothing is published until you read the draft and publish it.
          </p>
          <div className={microLabelCls}>{`Sections of the current version (${teaching.length})`}</div>
          {teaching.length === 0 ? (
            <p className={hintCls}>The current version has no sections to revise.</p>
          ) : (
            <ul className="space-y-1" data-testid="lms-builder-sections">
              {teaching.map((s) => (
                <li key={s.id} data-testid={`lms-builder-section-${s.id}`}>
                  <Checkbox
                    label={`${s.title || s.id}: revise with the new material`}
                    checked={value.replaceSectionIds.includes(s.id)}
                    onChange={(on) => toggle(s.id, on)}
                    testId={`lms-builder-revise-${s.id}`}
                  />
                </li>
              ))}
            </ul>
          )}
          <Checkbox
            label="Revise the closing section to cover the new material"
            checked={value.reviseClosing}
            onChange={(reviseClosing) => onChange({ ...value, reviseClosing })}
            testId="lms-builder-revise-closing"
          />
          <AudienceField value={value.audience} onChange={(audience) => onChange({ ...value, audience })} />
          <InstructionField value={value.instruction} onChange={(instruction) => onChange({ ...value, instruction })} />
          <p className={hintCls}>{profileHint(category)}</p>
        </section>
      </div>
    );
  }

  const { value, onChange } = props;
  const scenario = isScenarioCategory(value.category);
  const sections = sectionsImplied(value.category, value.targetMinutes);
  const draw = drawOf(value.category, value.poolSize);
  return (
    <div className="space-y-4" data-testid="lms-builder-steer">
      <section className={`${cardCls} space-y-3`} aria-labelledby={headingId}>
        <h3 id={headingId} className={subheadingCls}>
          {scenario ? 'The scenario' : 'The course'}
        </h3>
        <TextInput
          label="Title (optional)"
          value={value.title}
          onChange={(title) => onChange({ ...value, title })}
          placeholder="Blank: the builder proposes one from the documents"
          testId="lms-builder-title"
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <SelectField
            label="Category"
            value={value.category}
            onChange={(c) => onChange(withCategory(value, c as CourseCategory))}
            options={BUILDER_CATEGORY_ORDER.map((c) => ({ value: c, label: CATEGORY_LABELS[c] }))}
            testId="lms-builder-category"
            hint="SOP training is built from the SOP library, not here."
          />
          <SelectField
            label="Kind"
            value={value.kind}
            onChange={(k) => onChange({ ...value, kind: k as CourseKind })}
            options={BUILDER_KINDS.map((k) => ({ value: k, label: KIND_LABELS[k] }))}
            disabled={scenario}
            testId="lms-builder-kind"
            hint={scenario ? 'A scenario is a quiz.' : undefined}
          />
          {!scenario ? (
            <>
              <NumberInput
                label="Valid for (months)"
                value={value.validityMonths}
                onChange={(validityMonths) => onChange({ ...value, validityMonths })}
                min={1}
                max={120}
                allowEmpty
                placeholder="No expiry"
                hint="Empty means a completion never expires."
                testId="lms-builder-validity"
              />
              <NumberInput
                label="Renewal assigned (days before expiry)"
                value={value.renewalLeadDays}
                onChange={(renewalLeadDays) => onChange({ ...value, renewalLeadDays })}
                min={0}
                max={365}
                allowEmpty
                placeholder="Network default"
                testId="lms-builder-renewal"
              />
            </>
          ) : null}
          <SelectField
            label="Target length"
            value={String(value.targetMinutes)}
            onChange={(m) => onChange({ ...value, targetMinutes: Number(m) })}
            options={minutesOptions(value.category).map((m) => ({ value: String(m), label: `${m} minutes` }))}
            testId="lms-builder-minutes"
            hint={scenario ? 'A scenario is one section.' : `About ${sections} sections plus the closing section.`}
          />
          <SelectField
            label="Question pool"
            value={String(value.poolSize)}
            onChange={(p) => onChange({ ...value, poolSize: Number(p) })}
            options={poolOptions(value.category).map((p) => ({ value: String(p), label: `${p} questions` }))}
            testId="lms-builder-pool"
            hint={scenario ? 'Every question is asked (pass mark 100).' : `Each attempt draws ${draw}.`}
          />
        </div>
        <AudienceField value={value.audience} onChange={(audience) => onChange({ ...value, audience })} />
        <InstructionField value={value.instruction} onChange={(instruction) => onChange({ ...value, instruction })} />
        <p className={hintCls} data-testid="lms-builder-profile-hint">
          {profileHint(value.category)}
        </p>
      </section>
    </div>
  );
}

export default SteerStep;
